// The step that ends the business's setup inputs (hours, work days,
// handover, or the business phone for seats) runs the same write: the one
// idempotent writer (ensureBusiness), then the step's own write, then the
// shape step. A field profile refused reopens its own question; a field the
// step owns (onField) stays on the step with the server's sentence; anything
// else is the shell's error card.

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isApiError } from '@/lib/api';
import type { WizardError } from '../components/Wizard';
import { failureOf } from '../data/api';
import { ensureBusiness, reopenRoute } from '../data/enroll';
import { ONB } from '../data/flow';
import { onboardingStore } from '../state';

export type EnrollPhase = 'idle' | 'saving' | 'failed';

export interface EnrollRun {
  phase: EnrollPhase;
  failure: Pick<WizardError, 'kind' | 'ref' | 'detail'> | null;
  run: () => Promise<void>;
}

/**
 * `after` writes the step's own data once the business exists; `onField`
 * gets a 422 row (field, message) and returns true when the step shows it.
 */
export function useEnrollRun(after?: (businessId: string) => Promise<void>, onField?: (field: string, message: string) => boolean): EnrollRun {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<EnrollPhase>('idle');
  const [failure, setFailure] = useState<Pick<WizardError, 'kind' | 'ref' | 'detail'> | null>(null);
  const running = useRef(false);

  const run = async () => {
    if (running.current) return;
    running.current = true;
    setPhase('saving');
    setFailure(null);
    try {
      const id = await ensureBusiness();
      if (after) await after(id);
      // Written only now: a reload before this point comes back to this step (not to done).
      onboardingStore.set({ scheduled: true });
      navigate(ONB.shape);
    } catch (e) {
      const reopen = reopenRoute(e);
      if (reopen) {
        navigate(reopen.route, { state: { fieldError: reopen.message } });
        return;
      }
      if (onField && isApiError(e) && e.status === 422) {
        const row = (e.errors ?? []).find((r) => onField(r.field ?? '', r.message ?? ''));
        if (row) {
          setPhase('idle');
          return;
        }
      }
      setFailure(failureOf(e));
      setPhase('failed');
    } finally {
      running.current = false;
    }
  };

  return { phase, failure, run };
}
